import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal/Modal';
import { Button } from '../common/Button/Button';
import { Input } from '../common/Input/Input';
import { Select } from '../common/Select/Select';
import { User, CreateUserRequest, UserRole, UserStatus } from '../../types';
import { apiService } from '../../services/api.service';

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onSuccess: () => void;
}

export const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  user,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<CreateUserRequest>({
    name: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    middle_name: '',
    role: UserRole.VIEWER,
    status: UserStatus.PENDING_APPROVAL,
    employee_id: '',
    department: '',
    designation: '',
    phone: '',
    mobile: '',
    address: '',
    city: '',
    state: '',
    division: '',
    circle: '',
    zone: '',
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'contact' | 'organization'>('basic');

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        password: '', // Never pre-fill password
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        middle_name: user.middle_name || '',
        role: user.role,
        status: user.status,
        employee_id: user.employee_id || '',
        department: user.department || '',
        designation: user.designation || '',
        phone: user.phone || '',
        mobile: user.mobile || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        division: user.division || '',
        circle: user.circle || '',
        zone: user.zone || '',
      });
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        middle_name: '',
        role: UserRole.VIEWER,
        status: UserStatus.PENDING_APPROVAL,
        employee_id: '',
        department: '',
        designation: '',
        phone: '',
        mobile: '',
        address: '',
        city: '',
        state: '',
        division: '',
        circle: '',
        zone: '',
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create a copy of form data and handle password for updates
      let submitData: Partial<CreateUserRequest>;
      
      if (user && !formData.password) {
        // For updates, exclude password if not provided
        const { password, ...dataWithoutPassword } = formData;
        submitData = dataWithoutPassword;
      } else {
        // For creates or when password is provided
        submitData = { ...formData };
      }

      if (user) {
        await apiService.updateUser(user.id.toString(), submitData);
      } else {
        await apiService.createUser(submitData as CreateUserRequest);
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to save user:', error);
      // You might want to show an error toast here
      alert(error.response?.data?.message || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (key: keyof CreateUserRequest, value: any) => {
    setFormData({ ...formData, [key]: value });
  };

  const roleOptions = [
    { value: UserRole.VIEWER, label: 'Viewer' },
    { value: UserRole.EDITOR, label: 'Editor' },
    { value: UserRole.ASSISTANT_ENGINEER, label: 'Assistant Engineer' },
    { value: UserRole.EXECUTIVE_ENGINEER, label: 'Executive Engineer' },
    { value: UserRole.ENGINEER_IN_CHIEF, label: 'Engineer-in-Chief' },
    { value: UserRole.SYSTEM_ADMIN, label: 'System Administrator' },
  ];

  const statusOptions = [
    { value: UserStatus.ACTIVE, label: 'Active' },
    { value: UserStatus.PENDING_APPROVAL, label: 'Pending Approval' },
    { value: UserStatus.INACTIVE, label: 'Inactive' },
    { value: UserStatus.SUSPENDED, label: 'Suspended' },
    { value: UserStatus.RETIRED, label: 'Retired' },
    { value: UserStatus.TRANSFERRED, label: 'Transferred' },
  ];

  const telanganaDistricts = [
    'Adilabad', 'Bhadradri Kothagudem', 'Hyderabad', 'Jagtial', 'Jangaon', 
    'Jayashankar Bhupalpally', 'Jogulamba Gadwal', 'Kamareddy', 'Karimnagar', 
    'Khammam', 'Komaram Bheem Asifabad', 'Mahabubabad', 'Mahabubnagar', 
    'Mancherial', 'Medak', 'Medchal-Malkajgiri', 'Mulugu', 'Nagarkurnool', 
    'Nalgonda', 'Narayanpet', 'Nirmal', 'Nizamabad', 'Peddapalli', 
    'Rajanna Sircilla', 'Rangareddy', 'Sangareddy', 'Siddipet', 'Suryapet', 
    'Vikarabad', 'Wanaparthy', 'Warangal Rural', 'Warangal Urban', 'Yadadri Bhuvanagiri'
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={user ? 'Edit User' : 'Create User'}
      size="lg"
    >
      <div className="max-h-[80vh] overflow-y-auto">
        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            type="button"
            onClick={() => setActiveTab('basic')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'basic'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Basic Information
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('contact')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'contact'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Contact Details
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('organization')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'organization'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Organization
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="First Name"
                  value={formData.first_name || ''}
                  onChange={(e) => updateFormData('first_name', e.target.value)}
                />
                <Input
                  label="Middle Name"
                  value={formData.middle_name || ''}
                  onChange={(e) => updateFormData('middle_name', e.target.value)}
                />
                <Input
                  label="Last Name"
                  value={formData.last_name || ''}
                  onChange={(e) => updateFormData('last_name', e.target.value)}
                />
              </div>

              <Input
                label="Full Name"
                value={formData.name}
                onChange={(e) => updateFormData('name', e.target.value)}
                required
                placeholder="Enter full name"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  required
                  placeholder="user@pred.telangana.gov.in"
                />

                <Input
                  label="Employee ID"
                  value={formData.employee_id || ''}
                  onChange={(e) => updateFormData('employee_id', e.target.value)}
                  placeholder="EMP001"
                />
              </div>

              <Input
                label={user ? 'New Password (leave blank to keep current)' : 'Password'}
                type="password"
                value={formData.password}
                onChange={(e) => updateFormData('password', e.target.value)}
                required={!user}
                placeholder={user ? 'Leave blank to keep current password' : 'Enter password'}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Role"
                  options={roleOptions}
                  value={formData.role}
                  onChange={(e) => updateFormData('role', e.target.value as UserRole)}
                />

                <Select
                  label="Status"
                  options={statusOptions}
                  value={formData.status}
                  onChange={(e) => updateFormData('status', e.target.value as UserStatus)}
                />
              </div>
            </div>
          )}

          {/* Contact Details Tab */}
          {activeTab === 'contact' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Phone"
                  value={formData.phone || ''}
                  onChange={(e) => updateFormData('phone', e.target.value)}
                  placeholder="+91-40-23456789"
                />

                <Input
                  label="Mobile"
                  value={formData.mobile || ''}
                  onChange={(e) => updateFormData('mobile', e.target.value)}
                  placeholder="+91-9876543210"
                />
              </div>

              <Input
                label="Address"
                value={formData.address || ''}
                onChange={(e) => updateFormData('address', e.target.value)}
                placeholder="Complete address"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="City"
                  value={formData.city || ''}
                  onChange={(e) => updateFormData('city', e.target.value)}
                  placeholder="City name"
                />

                <Select
                  label="State"
                  options={[
                    { value: '', label: 'Select State' },
                    { value: 'Telangana', label: 'Telangana' },
                    { value: 'Andhra Pradesh', label: 'Andhra Pradesh' },
                  ]}
                  value={formData.state || ''}
                  onChange={(e) => updateFormData('state', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Organization Tab */}
          {activeTab === 'organization' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Department"
                  value={formData.department || ''}
                  onChange={(e) => updateFormData('department', e.target.value)}
                  placeholder="Panchayat Raj Engineering Department"
                />

                <Input
                  label="Designation"
                  value={formData.designation || ''}
                  onChange={(e) => updateFormData('designation', e.target.value)}
                  placeholder="Executive Engineer"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Division"
                  value={formData.division || ''}
                  onChange={(e) => updateFormData('division', e.target.value)}
                  placeholder="Division name"
                />

                <Input
                  label="Circle"
                  value={formData.circle || ''}
                  onChange={(e) => updateFormData('circle', e.target.value)}
                  placeholder="Circle name"
                />

                <Select
                  label="Zone"
                  options={[
                    { value: '', label: 'Select Zone' },
                    ...telanganaDistricts.map(district => ({
                      value: district,
                      label: district
                    }))
                  ]}
                  value={formData.zone || ''}
                  onChange={(e) => updateFormData('zone', e.target.value)}
                />
              </div>

              {/* Additional Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Additional Information</h4>
                <p className="text-xs text-gray-500">
                  All fields are optional but help in better organization and communication within the system.
                </p>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={loading} disabled={loading}>
              {loading ? 'Saving...' : user ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};